import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus, Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { consulta, pedir } from '../../api/nucleo'
import type { Lista } from '../../api/tipos'
import { Selector, claseDeEntrada, clases } from '../../componentes/ui'
import { useEsquema, type Campo } from '../esquema'
import { useFormulario, useValor } from '../Formulario'
import { Envoltura } from './Controles'

/**
 * Elegir un documento (o varios) de otra colección: una página madre, las categorías
 * de una entrada, el formulario de un bloque. Busca por el título de la colección
 * destino y **siempre dentro del sitio** (o del espacio, para lo que no es de una web,
 * como los usuarios): el acceso ya lo acota el núcleo, pero un desplegable que enseñe
 * las páginas de la web de al lado es un error aunque no se pueda guardar.
 *
 * Si el núcleo lo dice (`campo.crear`: categorías y etiquetas, para quien puede crearlas),
 * desde el mismo buscador se crea una nueva con solo su nombre: «Crear “Recetas”».
 */
type Referencia = { relationTo: string; value: number }
type Doc = { id: number; [k: string]: unknown }

const SIN_SITIO = new Set(['usuarios', 'clientes', 'planes'])

const filtroDe = (coleccion: string, sitio: null | number, espacio: number) => {
  if (coleccion === 'usuarios') return { 'where[clientes.cliente][equals]': espacio }
  if (SIN_SITIO.has(coleccion)) return {}
  return sitio ? { 'where[sitio][equals]': sitio } : { 'where[cliente][equals]': espacio }
}

const useTitulo = (coleccion: string) => useEsquema(coleccion).data?.titulo ?? 'id'

/** Si la colección cuelga de sí misma por un campo `padre` (las categorías): entonces cada una se nombra con su madre. */
const tienePadre = (campos: Campo[] | undefined, coleccion: string): boolean =>
  (campos ?? []).some((c) => (c.nombre === 'padre' && c.relacion === coleccion) || tienePadre(c.campos, coleccion) || (c.pestanas ?? []).some((p) => tienePadre(p.campos, coleccion)))

const useJerarquica = (coleccion: string) => {
  const { data } = useEsquema(coleccion)
  return tienePadre(data?.campos, coleccion)
}

/**
 * «Reparaciones › Portátiles»: en una colección con jerarquía, una categoría se nombra con
 * su madre, que es como se distinguen dos «Portátiles» de ramas distintas. La madre llega
 * poblada (`depth: 1`); si no, solo el nombre.
 */
const etiquetaDe = (doc: Doc | undefined, titulo: string): string => {
  if (!doc) return '…'
  const v = doc[titulo]
  const propio = typeof v === 'string' && v ? v : `#${doc.id}`
  const madre = doc.padre && typeof doc.padre === 'object' ? (doc.padre as Doc)[titulo] : null
  return typeof madre === 'string' && madre ? `${madre} › ${propio}` : propio
}

/** Los documentos ya elegidos, por id, para enseñar su título. */
const useElegidos = (coleccion: string, ids: number[]) => {
  const jerarquica = useJerarquica(coleccion)
  return useQuery({
    queryKey: ['relacion', coleccion, ids.join(','), jerarquica],
    enabled: ids.length > 0,
    queryFn: async () => (await pedir<Lista<Doc>>(`/${coleccion}${consulta({ 'where[id][in]': ids.join(','), limit: 100, depth: jerarquica ? 1 : 0 })}`)).docs,
  })
}

/**
 * El nombre de un documento por su id («Contacto», no «paginas #12»). Si ya no existe o
 * quien mira no lo puede leer, lo dice en vez de quedarse en «…».
 */
export const TituloDeDocumento = ({ coleccion, id }: { coleccion: string; id: number }) => {
  const titulo = useTitulo(coleccion)
  const { data, isPending } = useElegidos(coleccion, [id])
  if (isPending) return <>…</>
  const doc = data?.[0]
  return <>{doc ? etiquetaDe(doc, titulo) : 'No disponible'}</>
}

export const Buscador = ({
  coleccion,
  excluir,
  alElegir,
  autoFoco = false,
  crear = false,
  filtro,
  id,
}: {
  alElegir: (doc: Doc) => void
  autoFoco?: boolean
  coleccion: string
  /** Ofrecer «Crear “…”» con lo escrito, si no hay uno que se llame igual. */
  crear?: boolean
  excluir: number[]
  /** Condiciones de más para la búsqueda (`where[taxonomia][equals]`), además de la web. */
  filtro?: Record<string, number | string>
  /** El `id` del cuadro de búsqueda: la etiqueta del campo («Cuelga de») lo nombra. */
  id?: string
}) => {
  const { sitio, espacio } = useFormulario()
  const titulo = useTitulo(coleccion)
  // Hasta que llega el esquema no se sabe por qué campo buscar: con «id» se buscaría texto en un número.
  const conEsquema = Boolean(useEsquema(coleccion).data)
  const jerarquica = useJerarquica(coleccion)
  const clienteDeConsultas = useQueryClient()
  const [texto, setTexto] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [creando, setCreando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const { data } = useQuery({
    queryKey: ['buscar', coleccion, sitio, espacio, texto, jerarquica, filtro],
    enabled: abierto && conEsquema,
    queryFn: async () =>
      (
        await pedir<Lista<Doc>>(
          `/${coleccion}${consulta({ ...filtroDe(coleccion, sitio, espacio), ...filtro, ...(texto ? { [`where[${titulo}][contains]`]: texto } : {}), limit: 10, depth: jerarquica ? 1 : 0, sort: titulo })}`,
        )
      ).docs,
  })
  const caja = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const cerrar = (e: MouseEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierto(false)
    }
    document.addEventListener('mousedown', cerrar)
    return () => document.removeEventListener('mousedown', cerrar)
  }, [])
  const candidatos = (data ?? []).filter((d) => !excluir.includes(d.id))
  const escrito = texto.trim()
  // Se compara con su propio nombre, no con «Madre › Hija»: escribir «Portátiles» encuentra la que hay.
  const yaExiste = (data ?? []).some((d) => String(d[titulo] ?? '').trim().toLowerCase() === escrito.toLowerCase())
  const ofrecerCrear = crear && escrito !== '' && data !== undefined && !yaExiste

  const elegir = (d: Doc) => {
    alElegir(d)
    setTexto('')
    setAbierto(false)
    setFallo(null)
  }

  /** Crea el documento con solo su nombre, con los permisos de quien edita: si no puede, lo dice el núcleo. */
  const crearNuevo = async () => {
    setCreando(true)
    setFallo(null)
    try {
      const cuerpo: Record<string, unknown> = { [titulo]: escrito }
      if (!SIN_SITIO.has(coleccion)) {
        cuerpo.cliente = espacio
        if (sitio) cuerpo.sitio = sitio
      }
      const r = await pedir<{ doc: Doc }>(`/${coleccion}${consulta({ depth: 0 })}`, { metodo: 'POST', cuerpo })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['buscar', coleccion] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', coleccion] })
      elegir(r.doc)
    } catch (e) {
      setFallo(e instanceof Error ? e.message : 'No se ha podido crear.')
    } finally {
      setCreando(false)
    }
  }

  return (
    <div className="relative" ref={caja}>
      <Search aria-hidden className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue" />
      <input
        autoFocus={autoFoco}
        className={clases(claseDeEntrada(), 'pl-9')}
        id={id}
        onChange={(e) => {
          setTexto(e.target.value)
          setAbierto(true)
          setFallo(null)
        }}
        onFocus={() => setAbierto(true)}
        onKeyDown={(e) => {
          // Intro con un nombre nuevo lo crea, como en cualquier campo de etiquetas.
          if (e.key === 'Enter' && ofrecerCrear && candidatos.length === 0) {
            e.preventDefault()
            void crearNuevo()
          }
        }}
        placeholder={crear ? 'Busca o escribe uno nuevo…' : 'Busca por nombre…'}
        type="search"
        value={texto}
      />
      {abierto && (
        <ul className="absolute z-30 mt-1.5 max-h-64 w-full overflow-y-auto rounded-caja border border-linea bg-papel p-1 shadow-alta" role="listbox">
          {candidatos.length === 0 && !ofrecerCrear && <li className="px-3 py-2 text-[13px] text-tenue">{data ? 'Nada con ese nombre.' : 'Buscando…'}</li>}
          {candidatos.map((d) => (
            <li key={d.id}>
              <button className="w-full rounded-boton px-2.5 py-1.5 text-left text-[13px] hover:bg-papel-2" onClick={() => elegir(d)} type="button">
                {etiquetaDe(d, titulo)}
              </button>
            </li>
          ))}
          {ofrecerCrear && (
            <li>
              <button
                className="flex w-full items-center gap-1.5 rounded-boton px-2.5 py-1.5 text-left text-[13px] font-medium text-violeta hover:bg-violeta-suave disabled:opacity-60"
                disabled={creando}
                onClick={() => void crearNuevo()}
                type="button"
              >
                {creando ? <Loader2 aria-hidden className="size-3.5 animate-spin" /> : <Plus aria-hidden className="size-3.5" />}
                Crear «{escrito}»
              </button>
            </li>
          )}
        </ul>
      )}
      {fallo && (
        <p className="mt-1 text-xs text-error" role="alert">
          {fallo}
        </p>
      )}
    </div>
  )
}

const Ficha = ({ coleccion, id, quitar }: { coleccion: string; id: number; quitar?: () => void }) => (
  <span className="inline-flex h-8 items-center gap-1.5 rounded-boton border border-violeta-borde bg-violeta-suave pr-1.5 pl-2.5 text-[13px] font-medium text-violeta">
    <TituloDeDocumento coleccion={coleccion} id={id} />
    {quitar && (
      <button aria-label="Quitar" className="rounded p-0.5 text-violeta/70 hover:bg-papel hover:text-error" onClick={quitar} type="button">
        <X className="size-3.5" />
      </button>
    )}
  </span>
)

const idDe = (v: unknown): null | number => (typeof v === 'number' ? v : v && typeof v === 'object' && 'id' in v ? Number((v as Doc).id) : null)
const referenciaDe = (v: unknown): null | Referencia => {
  if (!v || typeof v !== 'object' || !('relationTo' in v)) return null
  const r = v as { relationTo: string; value: unknown }
  const id = idDe(r.value)
  return id === null ? null : { relationTo: r.relationTo, value: id }
}

export const Relacion = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { valor, cambiar, error } = useValor<unknown>(ruta)
  const { valores } = useFormulario()
  const colecciones = Array.isArray(campo.relacion) ? campo.relacion : [campo.relacion ?? '']
  const polimorfica = Array.isArray(campo.relacion)
  const [coleccionActiva, setColeccionActiva] = useState(colecciones[0] ?? '')
  // Un término cuelga de otro de su misma taxonomía (el núcleo lo exige al guardar): el buscador solo ofrece esos, y no crea.
  const taxonomiaDelTermino = campo.nombre === 'padre' && coleccionActiva === 'terminos' ? idDe(valores.taxonomia) : null
  const filtro = taxonomiaDelTermino !== null ? { 'where[taxonomia][equals]': taxonomiaDelTermino } : undefined

  // Lo elegido, normalizado a referencias {coleccion, id}.
  const elegidos: Referencia[] = (Array.isArray(valor) ? valor : valor === undefined || valor === null ? [] : [valor])
    .map((v) => (polimorfica ? referenciaDe(v) : idDe(v) === null ? null : { relationTo: colecciones[0] ?? '', value: idDe(v)! }))
    .filter((r): r is Referencia => r !== null)

  const escribir = (siguientes: Referencia[]) => {
    const aValor = (r: Referencia) => (polimorfica ? { relationTo: r.relationTo, value: r.value } : r.value)
    if (campo.varios) cambiar(siguientes.map(aValor))
    else cambiar(siguientes[0] ? aValor(siguientes[0]) : null)
  }

  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <div className="flex flex-col gap-2">
        {elegidos.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {elegidos.map((r) => (
              <Ficha
                coleccion={r.relationTo}
                id={r.value}
                key={`${r.relationTo}:${r.value}`}
                quitar={soloLectura ? undefined : () => escribir(elegidos.filter((e) => e !== r))}
              />
            ))}
          </div>
        )}
        {soloLectura && elegidos.length === 0 && <p className="text-[13px] text-tenue">Sin elegir.</p>}
        {!soloLectura && (campo.varios || elegidos.length === 0) && (
          <div className="flex gap-2">
            {polimorfica && (
              <Selector aria-label="En qué buscar" className="w-auto" onChange={(e) => setColeccionActiva(e.target.value)} value={coleccionActiva}>
                {colecciones.map((c) => (
                  <option key={c} value={c}>
                    {/* El nombre lo sirve el núcleo («Páginas»); nunca el identificador de la colección. */}
                    {campo.relacionEtiquetas?.[c] ?? c}
                  </option>
                ))}
              </Selector>
            )}
            <div className="flex-1">
              <Buscador
                alElegir={(d) => escribir(campo.varios ? [...elegidos, { relationTo: coleccionActiva, value: d.id }] : [{ relationTo: coleccionActiva, value: d.id }])}
                coleccion={coleccionActiva}
                crear={Boolean(campo.crear) && !polimorfica && !filtro}
                excluir={elegidos.filter((e) => e.relationTo === coleccionActiva).map((e) => e.value)}
                filtro={filtro}
                id={ruta}
              />
            </div>
          </div>
        )}
      </div>
    </Envoltura>
  )
}
